import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import StudentsTable, { Student } from '../components/Students/StudentsTable';
import AddStudentForm, { StudentFormData } from '../components/Students/AddStudentForm';
import { useStudentContext } from '../context/StudentContext';
import { fetchStudents, Student as APIStudent } from '../store/AdminSlices/studentSlice';
import { AppDispatch, RootState } from '../store/store';
import { TableSkeleton } from '../components/Skeleton';

const StudentsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { students, loading } = useSelector((state: RootState) => state.student);
  const [searchParams, setSearchParams] = useSearchParams();
  const { setIsAddingStudent, setBackHandler } = useStudentContext();
  const showAddForm = searchParams.get('add') === 'true';
  const [filteredStudents, setFilteredStudents] = useState<Student[]>([]);

  // Fetch students on mount
  useEffect(() => {
    dispatch(fetchStudents());
  }, [dispatch]);

  // Convert API students to table format and update filtered students
  useEffect(() => {
    if (students && Array.isArray(students) && students.length > 0) {
      const formattedStudents: Student[] = students.map((student: APIStudent) => {
        // Use name field if available, otherwise construct from first/last name
        const displayName = student.name && student.name.trim() 
          ? student.name 
          : `${student.first_name || ''} ${student.last_name || ''}`.trim() || student.email;
        
        return {
          id: student.id,
          name: displayName,
          email: student.email,
          status: (student.status || 'Not Started') as Student['status'],
          focusArea: student.focus_area || '-',
          completedSessions: student.completed_sessions || 0,
          lastSession: student.last_session 
            ? new Date(student.last_session).toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
              })
            : '-',
          coach_id: student.coach_id,
          assigned_coach: student.assigned_coach ? {
            id: student.assigned_coach.id,
            name: student.assigned_coach.name,
            avatar: student.assigned_coach.avatar,
            area_of_expertise: student.assigned_coach.area_of_expertise,
          } : undefined,
        };
      });
      setFilteredStudents(formattedStudents);
    }
  }, [students]);

  // Sync context state with URL query parameter
  useEffect(() => {
    setIsAddingStudent(showAddForm);
  }, [showAddForm, setIsAddingStudent]);

  // Set back handler for header back button
  useEffect(() => {
    setBackHandler(() => () => {
      setSearchParams({});
    });
  }, [setBackHandler, setSearchParams]);

  const handleAddStudent = () => {
    setSearchParams({ add: 'true' });
  };

  const handleCloseForm = () => {
    setSearchParams({});
  };

  const handleSubmitForm = (data: StudentFormData) => {
    // Refresh the student list after successful invite
    dispatch(fetchStudents());
    setSearchParams({});
  };

  const handleSearch = (query: string) => {
    if (!query.trim()) {
      if (students && Array.isArray(students) && students.length > 0) {
        const formattedStudents: Student[] = students.map((student: APIStudent) => {
          const displayName = student.name && student.name.trim() 
            ? student.name 
            : `${student.first_name || ''} ${student.last_name || ''}`.trim() || student.email;
          
          return {
            id: student.id,
            name: displayName,
            email: student.email,
            status: (student.status || 'Not Started') as Student['status'],
            focusArea: student.focus_area || '-',
            completedSessions: student.completed_sessions || 0,
            lastSession: student.last_session 
              ? new Date(student.last_session).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })
              : '-',
            coach_id: student.coach_id,
            assigned_coach: student.assigned_coach ? {
              id: student.assigned_coach.id,
              name: student.assigned_coach.name,
              avatar: student.assigned_coach.avatar,
              area_of_expertise: student.assigned_coach.area_of_expertise,
            } : undefined,
          };
        });
        setFilteredStudents(formattedStudents);
      }
      return;
    }

    const lowercaseQuery = query.toLowerCase();
    const filtered = filteredStudents.filter(
      (student) =>
        student.name.toLowerCase().includes(lowercaseQuery) ||
        student.email.toLowerCase().includes(lowercaseQuery) ||
        student.focusArea.toLowerCase().includes(lowercaseQuery) ||
        student.status.toLowerCase().includes(lowercaseQuery)
    );

    setFilteredStudents(filtered);
  };

  // Handle student ID from URL query parameter to auto-navigate to student profile
  useEffect(() => {
    const studentId = searchParams.get('id');
    if (studentId && filteredStudents.length > 0) {
      const student = filteredStudents.find(s => s.id === studentId);
      if (student) {
        navigate(`/students/${student.id}`, { replace: true });
      }
    }
  }, [searchParams, filteredStudents, navigate]);

  const handleViewProfile = (student: Student) => {
    navigate(`/students/${student.id}`);
  };

  const handleUpdateTarget = (student: Student) => {
    navigate(`/students/${student.id}?action=updateTarget`);
  };

  const handleMessage = (student: Student) => {
    // Navigate to messages page with student user_id
    // The MessagesPage will handle finding the conversation with this student
    navigate(`/messages?userId=${student.id}&userType=student`);
  };

  const handleAssignCoach = (student: Student) => {
    // Navigate to student detail page with assign coach action
    navigate(`/students/${student.id}?action=assignCoach`);
  };

  const handleChangeCoach = (student: Student) => {
    // Navigate to student detail page with change coach action
    navigate(`/students/${student.id}?action=changeCoach`);
  };

  if (showAddForm) {
    return (
      <AddStudentForm
        onClose={handleCloseForm}
        onSubmit={handleSubmitForm}
      />
    );
  }

  if (loading) {
    return (
      <div className="overflow-x-auto bg-white rounded-xl border border-[#F2F4F7] p-6 shadow-[0_4px_12px_0_rgba(249,250,251,1)]">
        <div className="mb-4 flex items-center justify-between">
          <div className="h-10 w-64 bg-gray-200 rounded-lg animate-pulse"></div>
          <div className="h-10 w-32 bg-gray-200 rounded-lg animate-pulse"></div>
        </div>
        <TableSkeleton rows={8} columns={7} />
      </div>
    );
  }

  return (
    <StudentsTable
      students={filteredStudents}
      onAddStudent={handleAddStudent}
      onViewProfile={handleViewProfile}
      onUpdateTarget={handleUpdateTarget}
      onMessage={handleMessage}
      onSearch={handleSearch}
      onAssignCoach={handleAssignCoach}
      onChangeCoach={handleChangeCoach}
      onRefresh={() => dispatch(fetchStudents())}
    />
  );
};

export default StudentsPage;
